import { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import FullscreenRoundedIcon from '@material-ui/icons/FullscreenRounded';

import { SText } from '@setvi/shared/components';
import { Document } from '@setvi/shared/components/sresource-children-list/components/page-preview';
import type { Resource } from '@setvi/shared/interfaces';
import { useDialog } from '@setvi/shared/providers';

import {
  PreviewZoomDialog,
  PREVIEW_ZOOM_DIALOG_ID
} from 'componentsV2/pdf-preview-dialog';
import type { PreviewPage } from 'componentsV2/pdf-preview-dialog';

import { useStyles } from './styles';

// Pager label for the asset's own PDF, shown before anything is generated.
const ASSET_LABEL = 'Asset';

interface PreviewPanelProps {
  /** What to render. Falls back to the asset's own PDF before anything is generated. */
  document: Resource;
  /** Pages to step through; a single page renders without a pager. */
  pages: PreviewPage[];
  /** False before anything has been generated, which shows the asset itself. */
  hasPreview: boolean;
  /** Hint under the asset preview while nothing has been generated. */
  emptyMessage: string;
  generating?: boolean;
  /** Something other than a generation is in flight (e.g. a list still loading). */
  loading?: boolean;
  pageIndex?: number;
  onPageChange?: (index: number) => void;
}

/**
 * The PDF preview shown beside the customization controls, shared by the
 * dialogs that generate one (QR / cover page, location).
 *
 * Before anything is generated the asset's own PDF is shown, pageable and
 * expandable like a generated one, with a hint underneath. Once generated, the
 * pager walks the generated pages the parent controls instead.
 *
 * The document stays mounted across states on purpose: remounting react-pdf
 * with a freshly generated url is unreliable, so the generating state is drawn
 * over it instead.
 */
export const PreviewPanel = ({
  document,
  pages,
  hasPreview,
  emptyMessage,
  generating = false,
  loading = false,
  pageIndex = 0,
  onPageChange
}: PreviewPanelProps) => {
  const classes = useStyles();
  const { openDialog, closeDialog } = useDialog();

  // The asset's own pages are paged here, since the parent only knows about
  // generated ones. Counts are kept per url so a generated PDF's count is never
  // mistaken for the asset's.
  const [assetPage, setAssetPage] = useState(1);
  const [pageCounts, setPageCounts] = useState<Record<string, number>>({});

  const busy = generating || loading;
  const canExpand = !busy;
  const assetUrl = document.DownloadURLWithSas;
  const assetPageCount = (assetUrl && pageCounts[assetUrl]) || 1;
  const currentAssetPage = Math.min(assetPage, assetPageCount);

  const activePage = pages[pageIndex];
  // The generated pages, or the asset as one whole-document entry the zoom
  // dialog pages through on its own.
  const zoomPages: PreviewPage[] = hasPreview
    ? pages
    : [{ label: ASSET_LABEL, pdfUrl: assetUrl }];

  const pager = hasPreview
    ? {
        show: !!onPageChange && !busy && pages.length > 1,
        label: `${activePage?.label} · page ${pageIndex + 1} of ${
          pages.length
        }`,
        prevDisabled: pageIndex === 0,
        nextDisabled: pageIndex === pages.length - 1,
        prev: () => onPageChange?.(pageIndex - 1),
        next: () => onPageChange?.(pageIndex + 1)
      }
    : {
        show: !busy && assetPageCount > 1,
        label: `${ASSET_LABEL} · page ${currentAssetPage} of ${assetPageCount}`,
        prevDisabled: currentAssetPage === 1,
        nextDisabled: currentAssetPage === assetPageCount,
        prev: () => setAssetPage(currentAssetPage - 1),
        next: () => setAssetPage(currentAssetPage + 1)
      };

  // Enlarged preview with zoom controls, opening on the page the pager is on.
  const openZoom = () => {
    if (!canExpand) return;
    openDialog(
      <PreviewZoomDialog
        title={document.Name}
        resource={document}
        pages={zoomPages}
        initialIndex={hasPreview ? pageIndex : 0}
        onClose={() => closeDialog(PREVIEW_ZOOM_DIALOG_ID)}
      />,
      { id: PREVIEW_ZOOM_DIALOG_ID }
    );
  };

  return (
    <>
      <Box className={classes.header}>
        <SText
          title="Preview"
          size="sm"
          weight="semibold"
          fontColor="#5D7083"
        />
        {canExpand && (
          <Tooltip title="Expand preview" placement="top">
            <IconButton size="small" onClick={openZoom}>
              <FullscreenRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box className={classes.box}>
        <Box
          className={clsx(classes.doc, canExpand && classes.expandable)}
          onClick={openZoom}>
          <Document
            document={document}
            loading={false}
            activePage={
              hasPreview ? (activePage?.pdfPage ?? 1) : currentAssetPage
            }
            loadingComponent={
              <Box className={classes.pdfLoading}>
                <CircularProgress size={28} className={classes.spinner} />
                <SText
                  title="Loading PDF…"
                  size="sm"
                  maxLines="unset"
                  fontColor="#5D7083"
                />
              </Box>
            }
            onLoadPages={numPages => {
              if (!assetUrl) return;
              setPageCounts(prev =>
                prev[assetUrl] === numPages
                  ? prev
                  : { ...prev, [assetUrl]: numPages }
              );
            }}
          />
        </Box>

        {busy && (
          <Box className={classes.overlay}>
            <CircularProgress size={28} className={classes.spinner} />
            <SText
              title={generating ? 'Generating…' : 'Loading…'}
              size="sm"
              maxLines="unset"
              fontColor="#5D7083"
            />
          </Box>
        )}
      </Box>

      {!busy && !hasPreview && (
        <SText
          className={classes.hint}
          title={emptyMessage}
          size="xs"
          maxLines="unset"
          fontColor="#5D7083"
        />
      )}

      {pager.show && (
        <Box className={classes.pager}>
          <IconButton
            className={classes.pagerButton}
            size="small"
            disabled={pager.prevDisabled}
            onClick={pager.prev}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <SText title={pager.label} size="sm" fontColor="#5D7083" />
          <IconButton
            className={classes.pagerButton}
            size="small"
            disabled={pager.nextDisabled}
            onClick={pager.next}>
            <ArrowForwardRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
};
